import React, { useState } from 'react';
import { useToggle } from 'react-use-v13';
import { Textarea } from 'react-scale/components/design-system/Form';
import { Checkbox } from 'design-system';
import SanitizedMarkdown from 'design-system/SanitizedMarkdown';
import { Label, Button } from 'design-system/Form';
import { WorkerTeamConfigProperties } from '@scale/shared/workerTeams/types';
import apiFetch, { defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';
import { invokeAsyncCorpJob } from '@scale/frontend-lib/invokeCorpJob';
import { useApiFetchGetRequest } from 'react-scale/hooks/useApiFetchGetRequest';
import {
  ADVANCED_CONFIG_NAMES,
  workerTeamConfigSchema,
  getWorkerTeamConfigSchemaForKey,
} from '@scale/shared/workerTeams/workerTeamConfig/schema';
import {
  CorpForm,
  CorpFormSubmitHandler,
} from 'react-scale/components/design-system/corp/CorpForm/CorpForm';
import _ from 'lodash';

const description = `
### Description:
- This tool allows you to assign Primary/Secondary assignments for a worker team (and optionally its descendants)

##### Author: Ahmad Elshenawy
---
`;

// @ts-ignore
const WorkerTeamAssignment: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [dummyTeam, setDummyTeam] = useState<string>('64112b3d08f770427c418897');
  const [workerTeamNamesRaw, setWorkerTeamNamesRaw] = useState<string>();
  const [workerTeams, setWorkerTeams] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [infoMessage, setInfoMessage] = useState<string>();
  const [jobResults, setJobResults] = useState<string>();
  // const [assignments, setAssignments] = useState<TeamAssignmentsResponse>();
  const [editablePrimary, toggleEditablePrimary] = useToggle(false);
  const [editableSeconary, toggleEditableSeconary] = useToggle(false);
  const [fetchDescendants, toggleFetchDescendants] = useToggle(false);

  const renderOutput = (text: string) => {
    return (
      <div className="response-markdown">
        <SanitizedMarkdown text={text || ''} />
        <style jsx global>{`
          .response-markdown tr {
            border: 1px solid rgba(0, 0, 0, 0.3);
          }

          .response-markdown tr:nth-child(even) {
            background-color: #f2f2f2;
          }

          .response-markdown th {
            border: 1px solid rgb(0, 0, 0, 0.3);
            padding: 6px 10px;
            background-color: #f2f2f2;
          }

          .response-markdown th {
            border: 1px solid rgba(255, 255, 255, 0.3);
            padding: 6px 10px;
            background-color: #222;
            color: white;
          }

          .response-markdown td {
            border: 1px solid rgba(0, 0, 0, 0.3);
            padding: 10px;
          }
        `}</style>
      </div>
    );
  };

  const enabled = workerTeamNamesRaw && !loading;

  const configRequest = useApiFetchGetRequest<WorkerTeamConfigProperties>(
    `corp-api/worker_teams/team/${dummyTeam}/config?v=1`,
  );

  // @ts-ignore TS2339
  const onSubmitAssignments: CorpFormSubmitHandler = async ({ formData, key }) => {
    setJobResults('');
    setLoading(true);

    if (
      !window.confirm(
        `Please confirm you wish to make these changes for the identified Worker Team(s) ${key}`,
      )
    ) {
      setLoading(false);
      return;
    }

    const success: Record<string, any> = {};
    const error: Record<string, any> = {};

    // eslint-disable-next-line @typescript-eslint/prefer-for-of
    for (let i = 0; i < workerTeams.length; i++) {
      const wt = workerTeams[i];
      const jobParams = {
        teamId: wt,
        key,
        value: formData,
        isClear: false,
      };
      try {
        const job = await invokeAsyncCorpJob('updateTeamConfigField', jobParams);
        success[wt] = job;
      } catch (err) {
        error[wt] = err;
      }
    }

    let resultMessage = `## Job Results\n\n#### ${key}\n\nSuccessfully updated ${key} with the following assignments\n> ${JSON.stringify(
      formData,
    )}\n\n| Worker Team| Status |\n|---|---|\n`;

    Object.entries(success).forEach(([teamId, job]) => {
      resultMessage += `|${teamId}| ✅ Success (https://dashboard.scale.com/corp/lookup/${job.id}) |\n`;
    });

    Object.entries(error).forEach(([teamId, err]) => {
      resultMessage += `|${teamId}| ❌ Error ${err}|\n`;
    });

    setJobResults(resultMessage);
    setLoading(false);
  };

  const configKeys = ['primaryTeamAssignments', 'secondaryTeamAssignments'];
  const configProperties = configRequest.response;
  const allConfigKeys = _.keys(workerTeamConfigSchema.properties);
  const basicConfigKeys = _.difference(allConfigKeys, ADVANCED_CONFIG_NAMES);
  const configsDisplay = configProperties
    ? _.map(configKeys ?? basicConfigKeys, (configKey, _i) => {
        const { title, ...configSchema } = getWorkerTeamConfigSchemaForKey(configKey);
        const formData: any[] = [];

        const isPrimaryTier = configKey === 'primaryTeamAssignments';

        const onEditSubmit: CorpFormSubmitHandler = async ({ formData }) => {
          if (!formData || !formData.length) {
            isPrimaryTier ? toggleEditablePrimary() : toggleEditableSeconary();
            return;
          }

          isPrimaryTier ? toggleEditablePrimary() : toggleEditableSeconary();
          // @ts-ignore TS2345
          return onSubmitAssignments({ formData, key: configKey });
        };

        return (
          <div>
            <h3>{configKey}</h3>
            <h4>{configSchema.description}</h4>
            {!(isPrimaryTier ? editablePrimary : editableSeconary) && (
              <>
                <Button
                  type="secondary"
                  onClick={isPrimaryTier ? toggleEditablePrimary : toggleEditableSeconary}
                >
                  Edit
                </Button>
              </>
            )}

            {(isPrimaryTier ? editablePrimary : editableSeconary) && (
              <CorpForm
                schema={configSchema}
                formData={formData as any}
                onSubmit={onEditSubmit}
                onCancel={isPrimaryTier ? toggleEditablePrimary : toggleEditableSeconary}
                submitLabel="Confirm Assignment(s)"
              />
            )}

            {!isPrimaryTier && editableSeconary && (
              <>
                <br />
                <br />
                <Button
                  type="success"
                  disabled={loading}
                  onClick={_e => {
                    // @ts-ignore TS2345
                    onSubmitAssignments({ formData: [], key: 'secondaryTeamAssignments' });
                  }}
                >
                  Clear Secondary Assignments
                </Button>
              </>
            )}
            <hr />
          </div>
        );
      })
    : [];

  const handleUpdate = async () => {
    setLoading(true);
    setWorkerTeams([]);
    setInfoMessage('Finding worker teams');

    if (!workerTeamNamesRaw || !workerTeamNamesRaw.trim()) {
      return;
    }

    const workerTeamNames = workerTeamNamesRaw.split(/\r?\n/).map(i => i.trim());

    try {
      const { matchedWorkerTeams, matchedWorkerTeamNames, failedToMatchWorkerTeams } =
        await apiFetch(null, 'corp-api/bulbasaur-utils/worker-teams-from-names', {
          method: 'PUT',
          body: { workerTeamNames, fetchDescendants },
        });

      let statusMessage: string = '';
      if (failedToMatchWorkerTeams && failedToMatchWorkerTeams.length) {
        statusMessage +=
          `| ❌ Failed to match ${failedToMatchWorkerTeams.length} Worker Team(s) |\n|---|\n` +
          failedToMatchWorkerTeams.map((i: string) => `| ${i} |`).join('\n');
      }

      if (matchedWorkerTeams && matchedWorkerTeams.length) {
        setWorkerTeams(matchedWorkerTeams);
        statusMessage +=
          `\n\n| ✅ Successfully identified ${matchedWorkerTeams.length} Worker Team(s) |\n|---|\n` +
          matchedWorkerTeamNames.map((i: string) => `| ${i} |`).join('\n');
      }

      setInfoMessage(statusMessage);
    } catch (error) {
      defaultApiFetchErrorHandler(error, { handleHttp: true });
    } finally {
      setLoading(false);
    }
  };

  const buttonText = () => {
    if (!workerTeamNamesRaw) return 'Specify one or more Worker Team names';
    return 'Find Team(s)!';
  };

  return (
    <div className="root">
      <section>
        <SanitizedMarkdown text={description} />
      </section>
      <section>
        <section>
          <Label>Worker Team Name(s):</Label>
          <i>Provide one or more Worker Team names below (each on its own line)</i>
          <br />
          <br />
          <Textarea
            // fullWidth
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
              setWorkerTeamNamesRaw(e.target.value);
            }}
            value={workerTeamNamesRaw}
            // onKeyDown={handleKeyDown}
            placeholder={'Worker Team names (each on its own line)'}
          ></Textarea>

          <Checkbox
            checked={fetchDescendants}
            label="Find and apply to all Child/Descendent Worker Teams"
            onChange={_value => {
              toggleFetchDescendants(!fetchDescendants);
            }}
          />
        </section>
      </section>
      <br />
      <section>
        <Button primary disabled={!enabled || loading} onClick={handleUpdate}>
          {buttonText()}
        </Button>
        <br />
        <br />
        {infoMessage && renderOutput(infoMessage)}
        <br />
        <br />
        <hr />
        {workerTeams && workerTeams.length && (
          <div>
            <div>{configsDisplay}</div>
          </div>
        )}

        {jobResults && renderOutput(jobResults)}
      </section>
      <style jsx>{`
        .root .two-columns {
          display: flex;
        }
        .root .two-columns section {
          flex-basis: 50%;
        }
        .root .two-columns section.disabled {
          color: rgba(0, 0, 0, 0.3);
        }
      `}</style>
    </div>
  );
};

export default WorkerTeamAssignment;
